/** @prettier */
import classNames from 'classnames';
import { pascalize } from 'humps';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import PwrBy from '@xanadu/ui-shared/components/PwrBy';
import Form from '@xanadu/ui-shared/components/widgets/Form';
import InputField from '@xanadu/ui-shared/components/widgets/InputField';
import translatedConstants, { ERROR_CODES, PRODUCTS } from '@xanadu/ui-shared/constants';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import {
  userForgotPassword as gtmUserForgotPassword,
  userVirtualPageView as gtmUserVirtualPageView,
} from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { getPlatform } from '@xanadu/ui-shared/interfaces/googleTagManager/api';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { displayModal as displayModalAct } from '@xanadu/ui-shared/modules/modal/actions';
import { updatePasswordRequest as updatePasswordRequestAct } from '@xanadu/ui-shared/modules/resetPassword/actions';
import { setTermsFields as setTermsFieldsAct } from '@xanadu/ui-shared/modules/termsAndConditions/actions';
import {
  setLoginError as setLoginErrorAct,
  showResetPassword as showResetPasswordAct,
} from '@xanadu/ui-shared/modules/user/actions';
import { registrationRequest as registrationRequestAct } from '@xanadu/ui-shared/modules/userRegistration/actions';
import {
  loginRequest as loginRequestAct,
  loginUser as loginUserAct,
} from '@xanadu/ui-shared/modules/userSession/actions';
import fetchService from '@xanadu/ui-shared/utils/fetchService';

import { getClientLogo } from '../../utils';

import IconClose from '../../../../images/icons/misc/close.svg';

import styles from './Login.module.scss';

const getLoginFields = () => ({
  username: {
    name: 'username',
    type: 'text',
    errors: [],
    label: 'Enter your username',
    value: '',
  },
  password: {
    name: 'password',
    type: 'password',
    label: 'Enter your password',
    errors: [],
    value: '',
  },
});

const getResetPasswordFields = () => ({
  username: {
    name: 'username',
    type: 'text',
    errors: [],
    label: 'Enter your username',
    value: '',
  },
});

const getTwoFactorAuthFields = () => ({
  'mfa-code': {
    name: 'mfa-code',
    type: 'text',
    errors: [],
    label: 'Enter your 2FA code',
    value: '',
  },
});

const Login = (props) => {
  const {
    loginImage = '',
    product,
    clientName,
    clientSite,
    loginUser,
    loginErrors,
    setLoginError,
    showResetPassword,
    showResetPasswordFn,
    displayModal,
    isLoggedIn,
    setTermsFields,
    loginRequest,
    onRequestClose,
    registrationRequest,
    userIsLocked,
    location,
    history,
  } = props;
  const [formType, setFormType] = useState('login');
  const [activeFields, setActiveFields] = useState(getLoginFields());
  const [twoFactorFields, setTwoFactorFields] = useState(getTwoFactorAuthFields());
  const [resetPasswordFields, setResetPasswordFields] = useState(getResetPasswordFields());
  const [internalLoginErrors, setInternalLoginErrors] = useState(loginErrors);
  const [controlsTwoFactorAuthEnabled, setControlsTwoFactorAuthEnabled] = useState(false);
  const [resetTimestamp, setResetTimestamp] = useState(null);
  const [twoFactorAuthText, setTwoFactorAuthText] = useState('');
  const [controlsResetPasswordEnabled, setControlsResetPasswordEnabled] = useState(false);
  const [controlsLoginEnabled, setControlsLoginEnabled] = useState(false);
  const [resetPasswordText, setResetPasswordText] = useState('');
  const [resetPasswordInternalErrors, setResetPasswordInternalErrors] = useState(false);
  const [requestStatusLogin, setRequestStatusLogin] = useState('default');
  const [requestStatusResetPassword, setRequestStatusResetPassword] = useState('default');

  const {
    customization: {
      Login: { backButtonType = 'primary', secondaryButtonInverted = true } = {},
    } = {},
  } = React.useContext(ChildrenPropsContext) || {};

  const { t } = useImprovedTranslation();

  const { ERROR_MESSAGES_BY_CODES = {} } = translatedConstants;

  const formatFieldsWithErrors = (currentFields, errors) =>
    errors.reduce((accumulator, item) => {
      const field = item.get('field') || 'username';
      const value = item.get('messages').first();
      const code = item.get('codes')?.first() || item.get('code');

      const newFields = {
        ...accumulator,
      };

      // shorten messages (until APIs do it)
      const computedMessage = ERROR_MESSAGES_BY_CODES[code] || value;

      const eligibleToBeDisplayed = code !== ERROR_CODES.LOGIN_MFA_REQUIRED;

      if (newFields[field] && eligibleToBeDisplayed) {
        newFields[field].errors = [computedMessage];
      }

      return newFields;
    }, currentFields);

  const timeoutResetSuccessFields = useRef(null);

  const ClientLogo = getClientLogo(clientName, clientSite, { wrapperClassName: styles.logo });
  const ClientLogoMobile = getClientLogo(clientName, clientSite, { forceText: true });

  let backgroundImage = {};

  const loginImageKey = `loginImage${product === PRODUCTS.exchange ? '' : pascalize(product.toLowerCase())}`;

  if (loginImage) {
    backgroundImage = { backgroundImage: `url(${props[loginImageKey]})` };
  }

  const onPropertyChange = (data) => {
    let formFields;

    if (formType === 'login') {
      formFields = activeFields;
      const primaryFieldValue = data.value;
      const secondaryFieldValue =
        data.name === 'username' ? formFields.password.value : formFields.username.value;
      if (primaryFieldValue && secondaryFieldValue) {
        setControlsLoginEnabled(true);
      } else {
        setControlsLoginEnabled(false);
      }
    } else if (formType === 'reset-password') {
      formFields = resetPasswordFields;
    } else if (formType === 'two-factor-auth') {
      formFields = twoFactorFields;
    }

    const newFormFields = Object.keys(formFields).reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: { ...accumulator[key] },
      }),
      formFields
    );

    newFormFields[data.name].value = data.value;

    if (formType === 'login') {
      setActiveFields(newFormFields);
    } else if (formType === 'reset-password') {
      setResetPasswordFields(newFormFields);
      setControlsResetPasswordEnabled(true);
    } else if (formType === 'two-factor-auth') {
      setControlsTwoFactorAuthEnabled(true);
      setTwoFactorFields(newFormFields);
    }
  };

  const getFormattedLoginFields = () => {
    // Takes Immutable list from this.loginErrors and returns correctly formatted array
    if (!internalLoginErrors) {
      return activeFields;
    }

    const currentFields = {
      username: {
        ...activeFields.username,
        errors: [],
      },
      password: {
        ...activeFields.password,
        errors: [],
      },
    };

    return formatFieldsWithErrors(currentFields, internalLoginErrors);
  };

  const getFormattedTwoFactorAuthFields = () => {
    // Takes Immutable list from this.loginErrors and returns correctly formatted array
    if (!internalLoginErrors) {
      return twoFactorFields;
    }

    const currentFields = {
      'mfa-code': {
        ...twoFactorFields['mfa-code'],
        errors: [],
      },
    };

    return formatFieldsWithErrors(currentFields, internalLoginErrors);
  };

  const getFormattedResetFields = () => {
    // Takes Immutable list from this.loginErrors and returns correctly formatted array
    if (!resetPasswordInternalErrors) {
      return resetPasswordFields;
    }

    const currentFields = {
      username: {
        ...resetPasswordFields.username,
        errors: [],
      },
    };

    return formatFieldsWithErrors(currentFields, resetPasswordInternalErrors);
  };

  const signIn = (withTwoFactor) => {
    const { username, password } = getFormattedLoginFields();
    let twoFactorAuthCode = {};
    let twoFactorAuthCodeValue = '';

    if (withTwoFactor) {
      const fields = getFormattedTwoFactorAuthFields();
      twoFactorAuthCode = fields['mfa-code'];
      twoFactorAuthCodeValue = twoFactorAuthCode.value
        ? twoFactorAuthCode.value.replace(/ /g, '')
        : '';
    }

    const payload = {
      loginType: 'login',
      loginPayload: {
        username: username.value,
        password: password.value,
        twoFactorAuthCode: twoFactorAuthCodeValue,
      },
    };

    loginUser(payload);

    setRequestStatusLogin('processing');
  };

  const resetPasswordRequest = () => {
    const now = Date.now();
    if (
      requestStatusResetPassword === 'processing' ||
      (resetTimestamp && now < resetTimestamp + 5000)
    ) {
      return;
    }

    setResetTimestamp(now);

    setResetPasswordInternalErrors(false);
    const url = '/bpapi/rest/security/v2/password-reset';
    const PUT_OPTIONS = {
      credentials: 'same-origin',
      method: 'POST',
      body: JSON.stringify({
        'user-identifier': resetPasswordFields.username.value,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    setRequestStatusResetPassword('processing');

    fetchService(url, PUT_OPTIONS).then(({ errors }) => {
      if (errors) {
        setControlsTwoFactorAuthEnabled(false);
        setRequestStatusResetPassword('failed');
        return;
      }

      // NOTE: We need to trigger the GTM event to send some data to dataLayerPush()
      gtmDispatch(gtmUserForgotPassword({ userForgot: resetPasswordFields.username.value }));

      setResetPasswordInternalErrors(false);
      setRequestStatusResetPassword('done');
      timeoutResetSuccessFields.current = setTimeout(() => {
        onRequestClose();
      }, 3000);
    });
  };

  const forgotPassword = () => {
    setFormType('reset-password');
    setInternalLoginErrors(false);
    setControlsTwoFactorAuthEnabled(false);
    setResetPasswordInternalErrors(false);
    setRequestStatusResetPassword('default');
  };

  const onJoinButtonClick = () => {
    loginRequest(false);

    setTimeout(() => {
      registrationRequest({
        show: true,
        promoRegistrationImage: null,
        checked: false,
      });

      // GTM
      gtmDispatch(
        gtmUserVirtualPageView({
          currentUrl: window.location.href,
          eventName: 'Reg Process',
          virtualPageTitle: 'Step 1 - account',
          virtualPageURL: '/reg/step1/',
          previousURL: '/login/',
          clickSource: { clickSource: `mb-join-now-button_${location.href}` },
        })
      );
      // ===
      track('Registration', 'Registration opened', {
        description: 'login',
      });
    }, 250);
  };

  const resetToLogin = () => {
    setFormType('login');
    setResetPasswordText('');
  };

  const getLoginForm = () => {
    const { username, password } = getFormattedLoginFields();

    return (
      <div className={styles.containerForm}>
        <Form
          type="login"
          primaryButtonEnabled={controlsLoginEnabled}
          contentPrimaryButton={t('Login')}
          onPrimaryButtonClick={() => signIn()}
          primaryButtonType={backButtonType}
          primaryButtonSizeType="wider"
          primaryDataHook="loginbutton"
          requestStatus={requestStatusLogin}
        >
          <InputField
            fullWidth
            label={t('Username')}
            name="username"
            theme="light"
            type="text"
            value={username.value}
            focusOnMount={getPlatform() === 'desktop'}
            onPropertyChange={onPropertyChange}
            error={username.errors.length ? username.errors : ''}
            dataHook="loginusername"
          />
          <InputField
            fullWidth
            label={t('Password')}
            name="password"
            theme="light"
            type="password"
            value={password.value}
            onPropertyChange={onPropertyChange}
            error={password.errors.length ? password.errors : ''}
            dataHook="loginpassword"
          />
        </Form>
        <button onClick={forgotPassword} data-hook="login-forgot-password">
          {t('Trouble Logging In?')}
        </button>
      </div>
    );
  };

  const getResetPasswordForm = () => {
    const { username } = getFormattedResetFields();

    const passwordResetSuccess = requestStatusResetPassword === 'done';
    let contentResetPasswordText = t(
      'If you are having trouble logging in, please enter your registered email address or username below.'
    );

    if (resetPasswordText) {
      contentResetPasswordText = resetPasswordText;
    }

    if (passwordResetSuccess) {
      contentResetPasswordText = t(
        'A password reset link has been sent to your registered e-mail address.'
      );
    }

    return (
      <div className={styles.containerForm}>
        <div className={styles.containerFormText} data-hook="reset-password-text">
          {contentResetPasswordText}
          <br />
          <br />
          {!resetPasswordText &&
            t('You will receive an email with your username, and a link to reset your password.')}
          {formType === 'reset-password' &&
            !passwordResetSuccess &&
            resetPasswordText &&
            t(
              'Enter your username below and we will send a reset link to your registered email address.'
            )}
        </div>
        <Form
          type="reset-password"
          primaryButtonEnabled={controlsResetPasswordEnabled}
          secondaryButtonEnabled
          contentPrimaryButton={resetPasswordText ? t('Unlock Account') : t('Recover Account')}
          contentSecondaryButton={t('Cancel')}
          onPrimaryButtonClick={resetPasswordRequest}
          onSecondaryButtonClick={resetToLogin}
          primaryButtonType={backButtonType}
          primaryButtonSizeType="wider"
          secondaryButtonType="flat"
          secondaryButtonSizeType="wider"
          requestStatus={requestStatusResetPassword}
          secondaryButtonInverted={secondaryButtonInverted}
          primaryDataHook="reset-password-submit-button"
          secondaryDataHook="reset-password-cancel-button"
        >
          <InputField
            fullWidth
            label={t('Email Address or Username')}
            name="username"
            focusOnMount={getPlatform() === 'desktop'}
            theme="light"
            type="text"
            value={username.value}
            onPropertyChange={onPropertyChange}
            error={!resetPasswordText && (username.errors.length ? username.errors : '')}
            dataHook="reset-password-username"
          />
        </Form>
      </div>
    );
  };

  const getTwoFactorAuthForm = () => {
    const { 'mfa-code': mfaCode } = getFormattedTwoFactorAuthFields();

    return (
      <div className={styles.containerForm}>
        <div className={styles.containerFormText}>{twoFactorAuthText}</div>
        <Form
          type="2fa"
          primaryButtonEnabled={controlsTwoFactorAuthEnabled}
          secondaryButtonEnabled
          contentPrimaryButton={t('Verify')}
          contentSecondaryButton={t('Cancel')}
          onPrimaryButtonClick={() => signIn(true)}
          onSecondaryButtonClick={() =>
            displayModal({
              show: false,
              section: 'default',
            })
          }
          primaryButtonType="primary"
          primaryButtonSizeType="wider"
          secondaryButtonType="flat"
          secondaryButtonSizeType="wider"
          requestStatus={requestStatusLogin}
          primaryDataHook="2fasubmit"
          secondaryDataHook="2facancel"
          secondaryButtonInverted={secondaryButtonInverted}
        >
          <InputField
            fullWidth
            label={t('Verification Code')}
            name="mfa-code"
            focusOnMount={getPlatform() === 'desktop'}
            theme="light"
            type="text"
            value={mfaCode.value}
            onPropertyChange={onPropertyChange}
            error={mfaCode.errors.length ? mfaCode.errors : ''}
            dataHook="2facode"
          />
        </Form>
      </div>
    );
  };

  const renderForm = () => {
    let component = null;

    switch (formType) {
      case 'login':
        component = getLoginForm();
        break;
      case 'reset-password':
        component = getResetPasswordForm();
        break;
      case 'two-factor-auth':
        component = getTwoFactorAuthForm();
        break;
      default:
        break;
    }

    return (
      <CSSTransition appear classNames={`sliding-${formType}`} timeout={400} key={formType}>
        {component}
      </CSSTransition>
    );
  };

  useEffect(() => {
    setInternalLoginErrors(loginErrors);
  }, [loginErrors]);

  useEffect(() => {
    if (internalLoginErrors) {
      const errorMessage =
        typeof internalLoginErrors === 'string' ? internalLoginErrors : internalLoginErrors.first();

      const field = errorMessage.get('field');

      if (field === 'tc-code') {
        setTermsFields({
          username: {
            value: activeFields.username.value,
          },
          password: {
            value: activeFields.password.value,
          },
        });
      }

      if (field === 'mfa-code') {
        setTwoFactorFields(getTwoFactorAuthFields());

        setFormType('two-factor-auth');

        setTwoFactorAuthText(
          <span data-hook="two-factor-auth-text">
            {t(
              'You have chosen to secure your account with 2 Factor Authentication. Please enter the code provided from your authentication app below.'
            )}
          </span>
        );

        setRequestStatusLogin('default');
      }

      if (errorMessage && typeof errorMessage !== 'string') {
        const message = errorMessage.get('messages').first();
        const code = errorMessage.get('codes') || [];
        if (message.indexOf('Incorrect multi factor') > -1) {
          // TODO: errors, ERROR_CODES
          setTwoFactorFields(getTwoFactorAuthFields());
          setFormType('two-factor-auth');

          setTwoFactorAuthText(
            t(
              'You have chosen to secure your account with 2 Factor Authentication. Please enter the code provided from your authentication app below.'
            )
          );

          setRequestStatusLogin('default');
        } else if (code.first() === ERROR_CODES.LOGIN_ACCOUNT_LOCKED_2) {
          setFormType('reset-password');
          setResetPasswordText(
            t('Your account has been locked due to three failed password attempts.')
          );
          setRequestStatusLogin('failed');
          setRequestStatusResetPassword('default');

          return;
        }
      }

      setRequestStatusLogin(field === 'mfa-code' ? 'default' : 'failed');
    } else if (isLoggedIn) {
      displayModal({
        show: false,
        section: 'default',
      });

      setControlsLoginEnabled(false);
      setActiveFields(getLoginFields());
      setRequestStatusLogin('default');
    } else if (!isLoggedIn && showResetPassword) {
      forgotPassword();
    }
  }, [internalLoginErrors, isLoggedIn, showResetPassword]);

  useEffect(() => {
    switch (formType) {
      case 'login':
        setActiveFields(getFormattedLoginFields());
        break;
      case 'reset-password':
        setResetPasswordFields(getFormattedResetFields());
        break;
      case 'two-factor-auth':
        setTwoFactorFields(getFormattedTwoFactorAuthFields());
        break;
      default:
        break;
    }
  }, [formType]);

  useEffect(
    () => () => {
      setLoginError(false);
      setFormType('login');

      const currentPath = location.pathname;
      if (currentPath.includes('/reset-password')) {
        showResetPasswordFn(false);
        history.push(currentPath.split('/reset-password')[0]);
      }
    },
    []
  );

  const loginClass = classNames({
    [styles.main]: true,
    [styles['main--hideRightSection']]: userIsLocked,
  });

  const leftSectionClass = classNames({
    [styles.section]: true,
    [styles['section--left']]: true,
  });

  const rightSectionClass = classNames({
    [styles.section]: true,
    [styles['section--right']]: true,
    [styles['section--dynamicImage']]: loginImage,
  });

  return (
    <div className={loginClass} data-hook="login-modal">
      <div className={leftSectionClass}>
        <button
          className={styles.closeButton}
          onClick={() => onRequestClose()}
          data-hook="login-close-button"
        >
          <IconClose />
        </button>
        <div className={styles.containerLogo}>
          <span>{t('Log In to')}&nbsp;</span>
          <ClientLogo />
        </div>
        {renderForm()}
        {!userIsLocked && (
          <Fragment>
            <button className={styles.joinButton} onClick={onJoinButtonClick}>
              {t('Join')} <ClientLogoMobile />
            </button>
            <PwrBy className={styles.poweredBy} hyperlinked />
          </Fragment>
        )}
      </div>
      {!userIsLocked && <div className={rightSectionClass} style={backgroundImage} />}
    </div>
  );
};

Login.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  product: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  clientSite: PropTypes.string.isRequired,
  loginImage: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  loginImageFutures: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  loginImagePredictions: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  loginImageZero: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  loginImageCasino: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  loginImageLiveCasino: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  loginImageVirtuals: PropTypes.string,
  loginErrors: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(List), PropTypes.string]),
  showResetPassword: PropTypes.bool,
  userIsLocked: PropTypes.bool.isRequired,
  displayModal: PropTypes.func.isRequired,
  setTermsFields: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  loginUser: PropTypes.func.isRequired,
  loginRequest: PropTypes.func.isRequired,
  registrationRequest: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  showResetPasswordFn: PropTypes.func.isRequired,
  setLoginError: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  loginUser: loginUserAct,
  loginRequest: loginRequestAct,
  registrationRequest: registrationRequestAct,
  displayModal: displayModalAct,
  setTermsFields: setTermsFieldsAct,
  updatePasswordRequest: updatePasswordRequestAct,
  showResetPasswordFn: showResetPasswordAct,
  setLoginError: setLoginErrorAct,
};

function mapStateToProps(state) {
  return {
    clientSite: state.getIn(['application', 'site']),
    clientName: state.getIn(['application', 'clientName']),
    loginImage: state.getIn(['application', 'loginImage']),
    loginImageFutures: state.getIn(['application', 'loginImageFutures']),
    loginImagePredictions: state.getIn(['application', 'loginImagePredictions']),
    loginImageZero: state.getIn(['application', 'loginImageZero']),
    loginImageCasino: state.getIn(['application', 'loginImageCasino']),
    loginImageLiveCasino: state.getIn(['application', 'loginImageLiveCasino']),
    loginImageVirtuals: state.getIn(['application', 'loginImageVirtuals']),
    product: state.getIn(['application', 'product']),
    loginErrors: state.getIn(['user', 'loginErrors']),
    showResetPassword: state.getIn(['user', 'showResetPassword']),
    isLoggedIn: state.getIn(['user', 'isLoggedIn']),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Login));
